import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { Header, LayoutContainer, NavBar, Pagination } from '../../components'
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { OrderDetail } from '../../components'
import { SelectStatus } from '../../components'
import { OrderModal } from '../../components'
import { orderStatuses } from '../../lib/statuses'
import { STATUSES, MODES } from '../../lib/constants'
import './OrderPage.scss'
import {
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Button,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from '@mui/material'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { gqlCommand, gqlClients } from '../../gql'
import { useQuery } from '@apollo/client'
import { formatDateStringForDisplay } from '../../lib/utils'

const OrderPage = () => {
  const { t } = useTranslation('order_page')
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [first, setFirst] = useState(rowsPerPage)
  const [last, setLast] = useState(null)
  const [after, setAfter] = useState(null)
  const [before, setBefore] = useState(null)

  // States for filters
  const [selectedClient, setSelectedClient] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [inputCity, setInputCity] = useState('')
  const [debouncedCity, setDebouncedCity] = useState('')
  const [startDate, setStartDate] = useState(undefined)
  const [endDate, setEndDate] = useState(undefined)

  // Set the startDate to midnight (00:00:00)
  const adjustStartOfDay = (date) => {
    const adjustedDate = new Date(date)
    adjustedDate.setHours(0, 0, 0, 0) // Set time to 00:00:00
    return adjustedDate
  }

  // Set the endDate to the end of the day (23:59:59)
  const adjustEndOfDay = (date) => {
    const adjustedDate = new Date(date)
    adjustedDate.setHours(23, 59, 59, 999) // Set time to 23:59:59
    return adjustedDate
  }

  //récupération des clients
  const {
    data: clients,
    error: clientsError,
    loading: clientsLoading,
  } = useQuery(gqlClients.CLIENTS)

  //Récupération des Commandes
  const {
    data,
    error: commandError,
    loading: commandLoading,
    refetch,
  } = useQuery(gqlCommand.COMMANDS, {
    variables: {
      first,
      after,
      before,
      last,
      clientId: selectedClient ? { equalTo: selectedClient } : undefined,
      status: selectedStatus ? { equalTo: selectedStatus } : undefined,
      startDate: startDate ? adjustStartOfDay(startDate).toISOString() : undefined,
      endDate: endDate ? adjustEndOfDay(endDate).toISOString() : undefined,
      city: debouncedCity ? { likeInsensitive: `%${debouncedCity.trim()}%` } : undefined, // Filter by city input (case-insensitive)
    },
  })
  const loading = clientsLoading || commandLoading
  const error = clientsError || commandError

  const [ordersState, setOrdersState] = useState([])
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState(MODES.CREATE)
  const [page, setPage] = useState(0)
  const [pageInfo, setPageInfo] = useState(null)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    if (data) {
      const transformedOrders = data.commands.edges.map(({ node }) => ({
        id: node.id,
        createdDate: formatDateStringForDisplay(node.createdAt),
        customerName: node.clientName,
        orderNumber: node.commandNumber,
        streetName: node.constructionSite?.address || node.address ,
        city: node.constructionSite?.city || node.city ,
        postCode: node.constructionSite?.postCode ,
        siteCode: node.constructionSite?.code,
        status: node.status,
        createdBy: `${node.userByCreatedBy.firstname} ${node.userByCreatedBy.lastname}`,
        contactName: node.contactName,
        contactPhone: node.contactPhone,
        dateDelivery: node.dateDelivery,
        customerId: node.clientId,
        comment: node.comment,
        constructionSiteId: node.constructionSiteId,
        deliveryAddress: node.constructionSite?.address || node.address,
        deliveryCity: node.constructionSite?.city || node.city,
        materials: node.commandMaterials.edges.map(({ node }) => ({
          id: node.id,
          materialId: node.material.id,
          family: node.material.productCategoryByIdProductCategory?.family,
          name: node.material.description,
          internalId: node.material.internalId,
          unit: node.material.measureUnitByBaseMeasureUnitCode?.abbr,
          quantity: node.quantity.toString(),
        })),
      })) 
      setOrdersState(transformedOrders)
      if (!selectedOrderDetail && transformedOrders.length > 0) {
        setSelectedOrderDetail(transformedOrders[0])
      }
      setPageInfo(data.commands.pageInfo)
      setTotalCount(data.commands.totalCount)
    }
  }, [data, selectedOrderDetail])
  //evite de lancer la recherche a chaque fois qu'on tape une lettre
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedCity(inputCity.trim())
    }, 500) // Délai de 500ms
    handler()
    return () => {
      handler.cancel()
    }
  }, [inputCity])
  let availableCustomers =
    clients?.clients.edges.map(({ node }) => ({
      id: node.id,
      displayName: node.displayName,
      internalId: node.internalId,
      active: node.active,
    })) || []
  const totalPages = Math.ceil(totalCount / rowsPerPage) ?? 0

  const handleDetailClick = (order) => {
    setSelectedOrderDetail(order)
  }

  const handleAddOrder = () => {
    setSelectedOrder({
      date: '',
      customerName: '',
      orderNumber: '',
      address: '',
      materials: [],
      status: STATUSES.INCOMPLETE,
    })
    setModalMode(MODES.CREATE)
    setIsModalOpen(true)
  }

  const handleEditClick = (order) => {
    setSelectedOrder(order)
    setModalMode(MODES.EDIT)
    setIsModalOpen(true)
  }

  const handleDuplicateClick = (order) => {
    setSelectedOrder({
      ...order,
      id: ordersState.length + 1,
      orderNumber: `DUP-${order.orderNumber}`,
      status: STATUSES.INCOMPLETE,
    })
    setModalMode(MODES.DUPLICATE)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedOrder(null)
  }

  const handleChangePage = (newPage, type) => {
    const isNext = type === 'next'

    setAfter(isNext ? pageInfo.endCursor : null)
    setBefore(isNext ? null : pageInfo.startCursor)
    setFirst(isNext ? rowsPerPage : null)
    setLast(isNext ? null : rowsPerPage)

    setPage(newPage)
  }

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage)
    setFirst(newRowsPerPage)
    setLast(null)
    setAfter(null)
    setBefore(null)
    setPage(0)
  }

  const handleRefreshList = async () => {
    try {
      await refetch()
    } catch (error) {
      console.error('Error refreshing list:', error)
    }
  }

  return (
    <div className="orders">
      <Header title={t('title')} />
      <NavBar active="orders" />
      <LayoutContainer>
        <Box sx={{ m: 1.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  {loading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="300px"
                    >
                      <CircularProgress
                        size={80}
                        sx={{
                          svg: {
                            width: '100%',
                            height: '100%',
                            viewBox: '0 0 40 40',
                          },
                        }}
                      />
                    </Box>
                  ) : error ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="300px"
                    >
                      <Typography color="error">{t('errorLoadingData')}</Typography>
                    </Box>
                  ) : (
                    <>
                      <Grid container justifyContent="flex-end" style={{ marginBottom: '3%' }}>
                        <Button variant="contained" color="primary" onClick={handleAddOrder}>
                          <span style={{ fontSize: '1.2rem', marginRight: '5px' }}>+</span>
                          {t('newOrder')}
                        </Button>
                      </Grid>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '80vh', // Full viewport height
                        }}
                      >
                        {/* Filter Section */}
                        <Grid container spacing={2} style={{ marginBottom: '3%' }}>
                          <Grid item xs={12} md={12}>
                            {t('sortBy')}
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                              <Select
                                value={selectedClient}
                                onChange={(event) => setSelectedClient(event.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">{t('clients')}</MenuItem>
                                {availableCustomers.map((customer) => (
                                  <MenuItem key={customer.id} value={customer.id}>
                                    {customer.displayName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                              <Select
                                value={selectedStatus}
                                onChange={(event) => setSelectedStatus(event.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">{t('status')}</MenuItem>
                                {orderStatuses().map((status) => (
                                  <MenuItem key={status.value} value={status.value}>
                                    {t(`${status.value}`)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label={t('city')}
                              variant="outlined"
                              value={inputCity}
                              onChange={(event) => setInputCity(event.target.value)}
                              placeholder={t('deliveryCity')}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('startDate')}
                                customInput={<TextField fullWidth />}
                              />
                              <span style={{ margin: '0 10px' }}> {t('to')} </span>
                              <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('endDate')}
                                customInput={<TextField fullWidth />}
                              />
                            </div>
                          </Grid>
                        </Grid>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                          <TableContainer component={Paper} sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            <Table>
                              <TableBody>
                                {totalCount > 0 ? (
                                  ordersState.map((order) => (
                                    <TableRow
                                      key={order.id}
                                      style={{
                                        backgroundColor:
                                          selectedOrderDetail?.id === order.id
                                            ? '#F0F8FF'
                                            : 'white', // Condition pour ajouter la bordure bleue
                                      }}
                                    >
                                      <TableCell>
                                        <div>{order.createdDate}</div>
                                        <div className="bold">{order.customerName}</div>
                                        <div>{order.orderNumber}</div>
                                      </TableCell>
                                      <TableCell>
                                        {order.streetName || order.city || order.postCode ? (
                                          <>
                                            {order.streetName && <div>{order.streetName}</div>}
                                            {order.city && <div>{order.city}</div>}
                                            {order.postCode && <div>{order.postCode}</div>}
                                          </>
                                        ) : (
                                          <div>{t('undefined')}</div>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <div
                                          style={{
                                            minWidth: '150px',
                                            maxHeight: '150px',
                                            overflowY: 'auto',
                                          }}
                                        >
                                          {order?.materials?.map((material, index) => (
                                            <div key={index}>
                                              <span className="bold">{material.internalId}</span> ,{' '}
                                              {material.quantity} {material.unit}
                                            </div>
                                          ))}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ m: 1.5 }}>
                                          <Link
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleDetailClick(order)}
                                          >
                                            {t('accessFile')}
                                          </Link>
                                        </Box>
                                        <div>
                                          <SelectStatus
                                            status={order.status}
                                            statuses={orderStatuses()}
                                            isDisabled={true}
                                          />
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div>
                                          <Button onClick={() => handleEditClick(order)}>
                                            <img
                                              src="icons/picto-edit.svg"
                                              alt="edit Icon"
                                              style={{ width: '24px', height: '24px' }}
                                            />
                                          </Button>
                                        </div>
                                        <div>
                                          <Button onClick={() => handleDuplicateClick(order)}>
                                            <img
                                              src="icons/picto-duplicate.svg"
                                              alt="edit Icon"
                                              style={{ width: '24px', height: '24px' }}
                                            />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={3}>
                                      <div style={{ textAlign: 'center', padding: '20px' }}>
                                        {t('noCommand')}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                        <Box sx={{ position: 'sticky', bottom: 0, background: 'white', zIndex: 1 }}>
                          <Pagination
                            page={page}
                            totalPages={totalPages}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {selectedOrderDetail && totalCount > 0 && (
              <Grid item xs={12} md={6}>
                {<OrderDetail data={selectedOrderDetail} />}
              </Grid>
            )}
            {selectedOrder && (
              <OrderModal
                open={isModalOpen}
                onClose={handleModalClose}
                order={selectedOrder}
                refresh={handleRefreshList}
                mode={modalMode}
              />
            )}
          </Grid>
        </Box>
      </LayoutContainer>
    </div>
  )
}

export default OrderPage
